import { useEffect, useMemo } from 'react';
// @mui
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Portal from '@mui/material/Portal';
import Backdrop from '@mui/material/Backdrop';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import * as Yup from 'yup';
import Iconify from 'src/components/iconify';
import Editor from 'src/components/editor';
import { useLocales } from 'src/locales';
import { IMailRecipient } from 'src/types/mail';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { MailsService } from 'src/api';
import { setError } from 'src/redux/slices/error';
import { useDispatch } from 'react-redux';
import { Avatar, Chip } from '@mui/material';

// ----------------------------------------------------------------------

const ZINDEX = 1998;

const POSITION = 24;

type Props = {
  onCloseCompose: VoidFunction;
  recipients?: IMailRecipient[];
};

export default function MailCompose({ onCloseCompose, recipients }: Props) {
  const smUp = useResponsive('up', 'sm');

  const { t } = useLocales();

  const dispatch = useDispatch();

  const fullScreen = useBoolean();

  const newEmailSchema = Yup.object().shape({
    body: Yup.string().required(t('Body is required')),
    subject: Yup.string().required(t('Subject is required')),
    sendTo: Yup.array().required().min(1),
  });

  const emailDefaultValues = useMemo(
    () => ({
      body: '',
      subject: '',
      sendTo: recipients ?? [],
    }),
    [recipients]
  );

  const emailMethods = useForm({
    resolver: yupResolver(newEmailSchema),
    defaultValues: emailDefaultValues,
  });

  const onEmailSubmit = emailMethods.handleSubmit(async (data) => {
    try {
      await MailsService.sendEmail({
        body: {
          html: data.body,
          subject: data.subject,
          sendTo: data.sendTo.map((m) => m.email!),
        },
      });

      emailMethods.reset();
      enqueueSnackbar(t('Email sent!'));
      onCloseCompose();
    } catch (error) {
      dispatch(setError(error));
    }
  });

  useEffect(() => emailMethods.reset(), [recipients, emailMethods]);

  const onEmailStateChange = (value: string, _delta: any, _source: any, _editor: any) => {
    emailMethods.setValue('body', value);
  };

  const emailContent = emailMethods.watch('body');

  return (
    <Portal>
      {(fullScreen.value || !smUp) && <Backdrop open sx={{ zIndex: ZINDEX }} />}

      <Paper
        sx={{
          right: 0,
          bottom: 0,
          borderRadius: 2,
          display: 'flex',
          position: 'fixed',
          zIndex: ZINDEX + 1,
          m: `${POSITION}px`,
          overflow: 'hidden',
          flexDirection: 'column',
          boxShadow: (theme) => theme.customShadows.dropdown,
          ...(fullScreen.value && {
            m: 0,
            right: POSITION / 2,
            bottom: POSITION / 2,
            width: `calc(100% - ${POSITION}px)`,
            height: `calc(100% - ${POSITION}px)`,
            minWidth: '579px',
          }),
        }}
      >
        <FormProvider methods={emailMethods} onSubmit={onEmailSubmit}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              bgcolor: 'background.neutral',
              p: (theme) => theme.spacing(1.5, 1, 1.5, 2),
            }}
          >
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('New Email')}
            </Typography>

            <IconButton onClick={fullScreen.onToggle}>
              <Iconify icon={fullScreen ? 'eva:collapse-fill' : 'eva:expand-fill'} />
            </IconButton>

            <IconButton onClick={onCloseCompose}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Stack>
          <Stack spacing={2} flexGrow={1} sx={{ p: 2 }}>
            <RHFAutocomplete
              key="recipients"
              name="sendTo"
              multiple
              disabled
              variant="standard"
              options={recipients ?? []}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name ?? '')}
              isOptionEqualToValue={(option, value) => option.email === value.email}
              label={`${t('To')}`}
              renderValue={(value, getItemProps) => (
                <>
                  {value.map((v) =>
                    typeof v === 'string' ? (
                      v
                    ) : (
                      <Chip
                        key={`chip-recipients-${v.email}`}
                        avatar={<Avatar alt={v.name} src={v.avatarUrl ?? ''} />}
                        label={v.name}
                        variant="outlined"
                        sx={{ mr: 1, mb: 1, mt: 1 }}
                      />
                    )
                  )}
                </>
              )}
            />

            <RHFTextField name="subject" label={t('Subject')} variant="standard" />

            <Editor
              simple
              id="compose-mail"
              value={emailContent}
              onChange={onEmailStateChange}
              placeholder={t('Type a message')}
              sx={{
                '& .ql-editor': {},
                ...(fullScreen.value && {
                  height: 1,
                  '& .quill': {
                    height: 1,
                  },
                  '& .ql-editor': {
                    maxHeight: 'unset',
                  },
                }),
              }}
            />

            <Stack direction="row" alignItems="center" justifyContent="right">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                endIcon={<Iconify icon="mdi:send" />}
                loading={emailMethods.formState.isSubmitting}
              >
                {t('Send')}
              </Button>
            </Stack>
          </Stack>
        </FormProvider>
      </Paper>
    </Portal>
  );
}
